import { useEffect, useState, useRef } from 'react';
import { X, Save, RefreshCw } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast } from '../ui/Toaster';
import { useDialogA11y } from '../ui/useDialogA11y';
import { calculateFinalAverage as calcFinalAvg } from '../../lib/grades';

interface Student {
  id: string;
  full_name: string;
}

interface Evaluation {
  id: string;
  alumno_id: string;
  modules_average: number;
  evaluation_1: number | null;
  evaluation_2: number | null;
  evaluation_3: number | null;
  final_evaluation: number | null;
  final_average: number;
}

interface EvaluationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  courseId: string;
  courseName: string;
}

export function EvaluationsModal({ isOpen, onClose, courseId, courseName }: EvaluationsModalProps) {
  const [students, setStudents] = useState<Student[]>([]);
  const [evaluations, setEvaluations] = useState<Record<string, Evaluation>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogA11y(isOpen, onClose, dialogRef);

  useEffect(() => {
    if (isOpen) {
      loadData();
      subscribeToSubmissions();
    }

    return () => {
      supabase.channel('module_submissions_evaluations').unsubscribe();
    };
  }, [isOpen, courseId]);

  const subscribeToSubmissions = () => {
    const channel = supabase
      .channel('module_submissions_evaluations')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'module_submissions',
        },
        async (payload) => {
          console.log('Module submission changed:', payload);
          if (payload.new && payload.new.alumno_id) {
            const alumnoId = payload.new.alumno_id;
            const newAvg = await calculateModulesAverage(alumnoId);

            setEvaluations(prev => {
              const updated = { ...prev };
              if (updated[alumnoId]) {
                updated[alumnoId] = {
                  ...updated[alumnoId],
                  modules_average: newAvg,
                  final_average: calculateFinalAverage({
                    ...updated[alumnoId],
                    modules_average: newAvg,
                  }),
                };
              }
              return updated;
            });
          }
        }
      )
      .subscribe();
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const { data: enrollments, error: enrollError } = await supabase
        .from('course_enrollments')
        .select('alumno_id, profiles:alumno_id(id, full_name)')
        .eq('course_id', courseId)
        .eq('status', 'active');

      if (enrollError) throw enrollError;

      const studentsList = enrollments?.map((e: any) => e.profiles).filter(Boolean) || [];
      setStudents(studentsList);

      const { data: evalsData, error: evalsError } = await supabase
        .from('course_evaluations')
        .select('*')
        .eq('course_id', courseId);

      if (evalsError) throw evalsError;

      const evalsMap: Record<string, Evaluation> = {};
      for (const student of studentsList) {
        const modulesAvg = await calculateModulesAverage(student.id);
        const existing = evalsData?.find((e: any) => e.alumno_id === student.id);

        if (existing) {
          evalsMap[student.id] = {
            ...existing,
            modules_average: modulesAvg,
          };
        } else {
          evalsMap[student.id] = {
            id: '',
            alumno_id: student.id,
            modules_average: modulesAvg,
            evaluation_1: null,
            evaluation_2: null,
            evaluation_3: null,
            final_evaluation: null,
            final_average: 0,
          };
        }
      }

      setEvaluations(evalsMap);
    } catch (error) {
      console.error('Error loading evaluations:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateModulesAverage = async (alumnoId: string): Promise<number> => {
    try {
      const { data: modules, error: modulesError } = await supabase
        .from('modules')
        .select('id')
        .eq('course_id', courseId);

      if (modulesError) throw modulesError;

      const moduleIds = modules?.map(m => m.id) || [];

      const { data: submissions, error: submissionsError } = await supabase
        .from('module_submissions')
        .select('grade')
        .eq('alumno_id', alumnoId)
        .in('module_id', moduleIds)
        .not('grade', 'is', null);

      if (submissionsError) throw submissionsError;

      const grades = submissions
        ?.map((s: any) => typeof s.grade === 'string' ? parseFloat(s.grade) : s.grade)
        .filter((g: number) => !isNaN(g) && g > 0) || [];

      if (grades.length === 0) return 0;

      const sum = grades.reduce((acc: number, g: number) => acc + g, 0);
      return parseFloat((sum / grades.length).toFixed(1));
    } catch (error) {
      console.error('Error calculating modules average:', error);
      return 0;
    }
  };

  // Single source of truth: lib/grades.ts (compartido con el sync BIM)
  const calculateFinalAverage = (evaluation: Evaluation): number => calcFinalAvg(evaluation);

  const handleInputChange = (alumnoId: string, field: keyof Evaluation, value: string) => {
    const numValue = value === '' ? null : parseFloat(value.replace(',', '.'));

    setEvaluations(prev => {
      const updated = { ...prev };
      const current = updated[alumnoId];

      if (field === 'evaluation_1' || field === 'evaluation_2' || field === 'evaluation_3' || field === 'final_evaluation') {
        current[field] = numValue;
      }

      current.final_average = calculateFinalAverage(current);

      return updated;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let hasErrors = false;
      const errors: string[] = [];

      for (const [alumnoId, evaluation] of Object.entries(evaluations)) {
        const payload = {
          course_id: courseId,
          alumno_id: alumnoId,
          modules_average: evaluation.modules_average,
          evaluation_1: evaluation.evaluation_1,
          evaluation_2: evaluation.evaluation_2,
          evaluation_3: evaluation.evaluation_3,
          final_evaluation: evaluation.final_evaluation,
          final_average: evaluation.final_average,
        };

        if (evaluation.id) {
          const { error } = await supabase
            .from('course_evaluations')
            .update(payload)
            .eq('id', evaluation.id);

          if (error) {
            hasErrors = true;
            errors.push(`Error al actualizar evaluación: ${error.message}`);
            console.error('Update error:', error);
          }
        } else {
          const { error } = await supabase
            .from('course_evaluations')
            .insert(payload);

          if (error) {
            hasErrors = true;
            errors.push(`Error al crear evaluación: ${error.message}`);
            console.error('Insert error:', error);
          }
        }
      }

      if (hasErrors) {
        toast.error('Error al guardar algunas evaluaciones: ' + errors.join(' · '));
      } else {
        toast.success('Evaluaciones guardadas');
        await loadData();
      }
    } catch (error) {
      console.error('Error saving evaluations:', error);
      toast.error('Error al guardar las evaluaciones: ' + (error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title-evaluations"
        tabIndex={-1}
        className="glass-surface rounded-xl w-full max-w-7xl max-h-[90vh] overflow-hidden border border-white/[0.06] flex flex-col"
      >
        <div className="flex items-center justify-between p-6 border-b border-white/[0.06]">
          <div>
            <h2 id="modal-title-evaluations" className="text-display text-2xl font-bold text-white">Evaluaciones del Curso</h2>
            <p className="text-muted mt-1">{courseName}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadData}
              disabled={loading}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
              title="Recalcular promedios de módulos"
            >
              <RefreshCw className={`w-5 h-5 text-cubus-lime ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-muted" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cubus-lime"></div>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-auto p-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="glass-deep sticky top-0 z-10">
                    <tr>
                      <th className="text-left p-3 text-white font-semibold border-r border-white/[0.08]">Alumno</th>
                      <th className="text-center p-3 text-white font-semibold border-r border-white/[0.08]">
                        Promedio Módulos<br />
                        <span className="text-xs text-muted">(10%)</span>
                      </th>
                      <th className="text-center p-3 text-white font-semibold border-r border-white/[0.08]">
                        Evaluación 1<br />
                        <span className="text-xs text-muted">(20%)</span>
                      </th>
                      <th className="text-center p-3 text-white font-semibold border-r border-white/[0.08]">
                        Evaluación 2<br />
                        <span className="text-xs text-muted">(20%)</span>
                      </th>
                      <th className="text-center p-3 text-white font-semibold border-r border-white/[0.08]">
                        Evaluación 3<br />
                        <span className="text-xs text-muted">(20%)</span>
                      </th>
                      <th className="text-center p-3 text-white font-semibold border-r border-white/[0.08]">
                        Evaluación Final<br />
                        <span className="text-xs text-muted">(30%)</span>
                      </th>
                      <th className="text-center p-3 text-white font-semibold">
                        Promedio Final
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((student) => {
                      const evaluation = evaluations[student.id];
                      if (!evaluation) return null;

                      return (
                        <tr key={student.id} className="border-b border-white/[0.06] hover:bg-white/[0.04]">
                          <td className="p-3 text-white font-medium border-r border-white/[0.08]">{student.full_name}</td>
                          <td className="p-3 text-center border-r border-white/[0.08]">
                            <span className="text-cubus-lime font-bold">{evaluation.modules_average.toFixed(1)}</span>
                          </td>
                          <td className="p-3 border-r border-white/[0.08]">
                            <input
                              type="number"
                              min="1.0"
                              max="7.0"
                              step="0.1"
                              value={evaluation.evaluation_1 ?? ''}
                              onChange={(e) => handleInputChange(student.id, 'evaluation_1', e.target.value)}
                              placeholder="1.0 - 7.0"
                              className="w-24 px-2 py-1 glass-deep border border-white/[0.1] rounded text-white text-center focus:outline-none focus:border-cubus-lime"
                            />
                          </td>
                          <td className="p-3 border-r border-white/[0.08]">
                            <input
                              type="number"
                              min="1.0"
                              max="7.0"
                              step="0.1"
                              value={evaluation.evaluation_2 ?? ''}
                              onChange={(e) => handleInputChange(student.id, 'evaluation_2', e.target.value)}
                              placeholder="1.0 - 7.0"
                              className="w-24 px-2 py-1 glass-deep border border-white/[0.1] rounded text-white text-center focus:outline-none focus:border-cubus-lime"
                            />
                          </td>
                          <td className="p-3 border-r border-white/[0.08]">
                            <input
                              type="number"
                              min="1.0"
                              max="7.0"
                              step="0.1"
                              value={evaluation.evaluation_3 ?? ''}
                              onChange={(e) => handleInputChange(student.id, 'evaluation_3', e.target.value)}
                              placeholder="1.0 - 7.0"
                              className="w-24 px-2 py-1 glass-deep border border-white/[0.1] rounded text-white text-center focus:outline-none focus:border-cubus-lime"
                            />
                          </td>
                          <td className="p-3 border-r border-white/[0.08]">
                            <input
                              type="number"
                              min="1.0"
                              max="7.0"
                              step="0.1"
                              value={evaluation.final_evaluation ?? ''}
                              onChange={(e) => handleInputChange(student.id, 'final_evaluation', e.target.value)}
                              placeholder="1.0 - 7.0"
                              className="w-24 px-2 py-1 glass-deep border border-white/[0.1] rounded text-white text-center focus:outline-none focus:border-cubus-lime"
                            />
                          </td>
                          <td className="p-3 text-center">
                            {(() => {
                              // Contar cuántas evaluaciones tiene ingresadas para indicar
                              // visualmente si el promedio es parcial (faltan notas) o
                              // total (todas las 5 columnas con valor).
                              const valuesIngresadas = [
                                evaluation.modules_average,
                                evaluation.evaluation_1,
                                evaluation.evaluation_2,
                                evaluation.evaluation_3,
                                evaluation.final_evaluation,
                              ].filter(v => v != null && v > 0).length;
                              const totalEvals = 5;
                              const isParcial = valuesIngresadas > 0 && valuesIngresadas < totalEvals;
                              const isVacio   = valuesIngresadas === 0;
                              return (
                                <div className="flex flex-col items-center">
                                  <span className={`font-bold text-lg ${
                                    isVacio   ? 'text-white/30'
                                    : isParcial ? 'text-amber-400'
                                    :             'text-cubus-lime'
                                  }`}>
                                    {evaluation.final_average.toFixed(1)}
                                  </span>
                                  {isParcial && (
                                    <span className="text-[10px] text-amber-400/70 mt-0.5">
                                      parcial · {valuesIngresadas}/{totalEvals}
                                    </span>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="p-6 border-t border-white/[0.06] flex justify-end gap-3">
              <button
                onClick={onClose}
                className="px-6 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 bg-cubus-lime text-cubus-bg rounded-lg hover:bg-cubus-lime-dim transition-colors font-semibold flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white/[0.08]"></div>
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Guardar Evaluaciones
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
